import capitalize from 'lodash/capitalize';

import { Box, Flex, Text } from '@vimeo/bokeh';
import { getCurrentLocale, translate } from '@vimeo/i18n-runtime';
import { type PaywallTrackingProps } from '@vimeo/picox';
import { useGetSubscriptionPlansData } from '@vimeo/subscription-api-plans-data-hook';

import { ComparisonPaywallHeader, ComparisonPlanCard, OtherPlansButton } from '../components';
import {
  getCardEntitlementsForTier,
  getComparisonFeatures,
  getFeatureValue,
  getSeatCountLabel,
  getStorageLabel,
  getSubtitleForTier,
} from './comparisonFeatures';

interface CardComparisonPaywallProps {
  tier: 'starter' | 'standard' | 'advanced';
  currentTier?: 'free' | 'starter' | 'standard' | 'advanced';
  paywallTracking: PaywallTrackingProps;
  isUserEligibleForFreeTrial?: boolean;
}

export function CardComparisonPaywall({
  tier,
  currentTier,
  paywallTracking,
  isUserEligibleForFreeTrial = false,
}: CardComparisonPaywallProps) {
  const plans = useGetSubscriptionPlansData(
    ['free', 'starter', 'standard', 'advanced'],
    undefined,
    false,
  );
  const currentPlan =
    plans?.find((plan) => plan.tier === currentTier) ??
    plans?.find((plan) => plan.metadata?.interactions?.purchase?.status === 'purchased');
  const targetPlan =
    plans?.find((plan) => plan.tier === tier) ??
    plans?.find(
      (plan) => plan.tier === 'starter' || plan.tier === 'standard' || plan.tier === 'advanced',
    );
  const hasTrialPath = Boolean(targetPlan?.metadata?.interactions?.purchase?.uri?.freeTrial);
  const isTrial = hasTrialPath && isUserEligibleForFreeTrial;

  const isLoaded = Boolean(targetPlan);
  const currency =
    targetPlan?.currency?.currencyCode ?? currentPlan?.currency?.currencyCode ?? 'USD';
  const currentPrice =
    currentPlan?.tier === 'free'
      ? formatCurrencyAmount(0, currency)
      : (currentPlan?.metadata?.purchasedProduct?.displayPriceFormatted ??
        currentPlan?.priceFormatted?.annualMonthly ??
        '');
  const targetPrice = targetPlan?.priceFormatted?.annualMonthly ?? '';
  const targetMonthlyPrice = targetPlan?.priceFormatted?.monthly ?? '';
  const targetDisplayName = targetPlan?.name ?? capitalize(`${tier}`);
  const currentDisplayName = currentPlan?.name ?? '';
  const currentSubtitle = getSubtitleForTier(currentPlan?.tier);
  const targetSubtitle = getSubtitleForTier(targetPlan?.tier ?? tier);
  const comparisonKeyFeaturesLabel = translate({
    singular: 'Everything in {CURRENT_TIER}, plus:',
    replacements: {
      CURRENT_TIER: currentDisplayName || translate('your current plan'),
    },
  });
  const comparisonFeatures = getComparisonFeatures({
    currentPlan,
    targetPlan,
    currentTier: currentPlan?.tier,
    targetTier: tier,
  });
  const currentSeatCount = getSeatCountLabel(
    getFeatureValue(comparisonFeatures, 'seats', 'currentValue'),
  );
  const targetSeatCount = getSeatCountLabel(
    getFeatureValue(comparisonFeatures, 'seats', 'targetValue'),
  );
  const currentStorageAmount = getStorageLabel(
    getFeatureValue(comparisonFeatures, 'storage', 'currentValue'),
  );
  const targetStorageAmount = getStorageLabel(
    getFeatureValue(comparisonFeatures, 'storage', 'targetValue'),
  );
  const targetEntitlements = getCardEntitlementsForTier(targetPlan?.tier ?? tier);
  const currentEntitlements = getCardEntitlementsForTier(currentPlan?.tier);

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      maxWidth="600px"
      gap="24px"
      pt={{ base: '24px', md: '0' }}
      pb={{ base: '40px', md: '24px' }}
      mx="auto"
    >
      <ComparisonPaywallHeader tierName={targetDisplayName} />

      {/* Desktop: horizontal overlapping cards */}
      <Flex display={{ base: 'none', md: 'flex' }} alignItems="stretch" pr="39px" width="100%">
        {/* Current card — pt offsets for the Recommended banner height on the target card */}
        <Flex flex="1" mr="-39px" pt="24px" minWidth="0" direction="column">
          <ComparisonPlanCard
            planName={currentDisplayName}
            description={currentSubtitle}
            price={currentPrice}
            seatCount={currentSeatCount}
            seatIcon={<PersonIcon />}
            storageAmount={currentStorageAmount}
            keyFeaturesLabel={translate('Key features')}
            entitlements={currentEntitlements}
            isCurrentPlan
            fillHeight
          />
        </Flex>

        {/* Target card */}
        <Flex flex="1" mr="-39px" minWidth="0" zIndex={1} direction="column">
          <ComparisonPlanCard
            planName={targetDisplayName}
            description={targetSubtitle}
            price={targetPrice}
            billingInfo={
              <Flex direction="column" fontSize="body-sm" color="text-secondary" lineHeight="1.2">
                <Text fontSize="body-sm" color="text-secondary">
                  {translate('per month, billed annually')}
                </Text>
                {targetMonthlyPrice ? (
                  <Text fontSize="body-sm" color="text-secondary">
                    {translate({
                      singular: 'or {PRICE} billed monthly',
                      replacements: { PRICE: targetMonthlyPrice },
                    })}
                  </Text>
                ) : null}
              </Flex>
            }
            seatCount={targetSeatCount}
            seatIcon={<UsersIcon />}
            storageAmount={targetStorageAmount}
            keyFeaturesLabel={comparisonKeyFeaturesLabel}
            entitlements={targetEntitlements}
            isRecommended
            isDisabled={!isLoaded}
            paywallTracking={paywallTracking}
            subscriptionTier={tier}
            isTrial={isTrial}
            fillHeight
          />
        </Flex>
      </Flex>

      {/* Mobile: target card shown first, scroll down to reveal current card */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        direction="column"
        gap="md"
        width="100%"
        px="md"
        mt="sm"
      >
        {/* Target card — primary, shown first */}
        <Box>
          <ComparisonPlanCard
            planName={targetDisplayName}
            description={targetSubtitle}
            price={targetPrice}
            billingInfo={
              <Flex direction="column" fontSize="body-sm" color="text-secondary" lineHeight="1.2">
                <Text fontSize="body-sm" color="text-secondary">
                  {translate('per month, billed annually')}
                </Text>
                {targetMonthlyPrice ? (
                  <Text fontSize="body-sm" color="text-secondary">
                    {translate({
                      singular: 'or {PRICE} billed monthly',
                      replacements: { PRICE: targetMonthlyPrice },
                    })}
                  </Text>
                ) : null}
              </Flex>
            }
            seatCount={targetSeatCount}
            seatIcon={<UsersIcon />}
            storageAmount={targetStorageAmount}
            keyFeaturesLabel={comparisonKeyFeaturesLabel}
            entitlements={targetEntitlements}
            isRecommended
            isDisabled={!isLoaded}
            paywallTracking={paywallTracking}
            subscriptionTier={tier}
            isTrial={isTrial}
          />
        </Box>

        {/* Current card — scroll down to compare */}
        <Box>
          <ComparisonPlanCard
            planName={currentDisplayName}
            description={currentSubtitle}
            price={currentPrice}
            seatCount={currentSeatCount}
            seatIcon={<PersonIcon />}
            storageAmount={currentStorageAmount}
            keyFeaturesLabel={translate('Key features')}
            entitlements={currentEntitlements}
            isCurrentPlan
          />
        </Box>
      </Flex>

      <Flex width="100%" maxWidth="400px" mx="auto" pb={{ base: 'sm', md: '0' }}>
        <OtherPlansButton paywallTracking={paywallTracking} />
      </Flex>
    </Flex>
  );
}

function PersonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8ZM8 9C5.79086 9 2 10.1193 2 12.5V14H14V12.5C14 10.1193 10.2091 9 8 9Z"
        fill="currentColor"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 7C7.38071 7 8.5 5.88071 8.5 4.5C8.5 3.11929 7.38071 2 6 2C4.61929 2 3.5 3.11929 3.5 4.5C3.5 5.88071 4.61929 7 6 7ZM10 7C11.1046 7 12 6.10457 12 5C12 3.89543 11.1046 3 10 3C9.44772 3 8.94772 3.22386 8.58579 3.58579C9.16064 4.16064 9.5 4.94772 9.5 5.5C9.5 6.05228 9.16064 6.83936 8.58579 7.41421C8.94772 7.77614 9.44772 8 10 8V7ZM6 8C3.79086 8 1 9.11929 1 11V13H11V11C11 9.11929 8.20914 8 6 8ZM11 8.5C12.6569 9.34315 14 10.1193 14 11.5V13H12V11C12 9.93913 11.6839 9.11929 11 8.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function formatCurrencyAmount(amount: number, currencyCode: string): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
